import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { colors } from '../styles/styles';
import useWindowResize from '../hooks/useWindowResize';
import useAllThoughts from '../hooks/useAllThoughts';

const Star = ({ x, y, size, thought, onStarClick }) => {
    const [twinkle, setTwinkle] = useState(1);
    const [hover, setHover] = useState(false);
    const [color] = useState(() => {
        // Generate a subtle random color variation
        const hue = Math.random() * 60 - 30; // variation between -30 and +30
        const saturation = Math.random() * 20; // 0-20% saturation
        return `hsl(${60 + hue}, ${saturation}%, 100%)`; // Base is 60 (yellow-ish)
    });

    useEffect(() => {
        const interval = setInterval(() => {
            setTwinkle(Math.random() * 0.5 + 0.5);
        }, Math.random() * 3000 + 1000);

        return () => clearInterval(interval);
    }, []);

    return (
        <div
            onClick={() => onStarClick(thought)}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
                position: 'absolute',
                left: x,
                top: y,
                width: size * 2,
                height: size * 2,
                cursor: 'pointer',
                transition: 'all 0.3s ease-in-out',
                transform: hover ? `scale(1.5)` : 'scale(1)',
                zIndex: hover ? 2 : 1,
            }}
        >
            {/* Core of the star */}
            <div style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                width: size * 0.5,
                height: size * 0.5,
                transform: 'translate(-50%, -50%)',
                backgroundColor: color,
                borderRadius: '50%',
                opacity: twinkle,
                boxShadow: `
                    0 0 ${size * 0.5}px ${size * 0.25}px ${color},
                    0 0 ${size}px ${size * 0.5}px rgba(255, 255, 255, 0.5),
                    0 0 ${size * 2}px ${size}px rgba(255, 255, 255, 0.3)
                `,
            }} />

            {/* Horizontal ray */}
            <div style={{
                position: 'absolute',
                left: '0',
                top: '50%',
                width: '100%',
                height: size * 0.15,
                transform: 'translateY(-50%)',
                background: `linear-gradient(
                    90deg,
                    transparent 0%,
                    rgba(255, 255, 255, ${twinkle * 0.2}) 40%,
                    rgba(255, 255, 255, ${twinkle * 0.2}) 60%,
                    transparent 100%
                )`,
                opacity: hover ? 1 : twinkle * 0.7,
            }} />

            {/* Vertical ray */}
            <div style={{
                position: 'absolute',
                top: '0',
                left: '50%',
                width: size * 0.15,
                height: '100%',
                transform: 'translateX(-50%)',
                background: `linear-gradient(
                    0deg,
                    transparent 0%,
                    rgba(255, 255, 255, ${twinkle * 0.2}) 40%,
                    rgba(255, 255, 255, ${twinkle * 0.2}) 60%,
                    transparent 100%
                )`,
                opacity: hover ? 1 : twinkle * 0.7,
            }} />

            {/* Diagonal rays */}
            <div style={{
                position: 'absolute',
                left: '0',
                top: '0',
                width: '100%',
                height: '100%',
                transform: 'rotate(45deg)',
            }}>
                <div style={{
                    position: 'absolute',
                    left: '0',
                    top: '50%',
                    width: '100%',
                    height: size * 0.15,
                    transform: 'translateY(-50%)',
                    background: `linear-gradient(
                        90deg,
                        transparent 0%,
                        rgba(255, 255, 255, ${twinkle * 0.1}) 40%,
                        rgba(255, 255, 255, ${twinkle * 0.1}) 60%,
                        transparent 100%
                    )`,
                    opacity: hover ? 1 : twinkle * 0.5,
                }} />
                <div style={{
                    position: 'absolute',
                    top: '0',
                    left: '50%',
                    width: size * 0.15,
                    height: '100%',
                    transform: 'translateX(-50%)',
                    background: `linear-gradient(
                        0deg,
                        transparent 0%,
                        rgba(255, 255, 255, ${twinkle * 0.1}) 40%,
                        rgba(255, 255, 255, ${twinkle * 0.1}) 60%,
                        transparent 100%
                    )`,
                    opacity: hover ? 1 : twinkle * 0.5,
                }} />
            </div>
        </div>
    );
};

const ThoughtPreview = ({ thought, onClose }) => {
    const isMobile = useWindowResize();
    if (!thought) return null;

    const lines = thought.thought.split('\n');
    const title = lines[0];
    const content = lines.slice(1).join('\n');

    return (
        <div
            style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: colors.background,
                padding: '20px',
                paddingTop: '16px',
                borderRadius: '15px',
                maxWidth: isMobile ? '90%' : '80%',
                maxHeight: '80vh',
                width: isMobile ? '90%' : 'auto',
                overflow: 'auto',
                boxShadow: '0 0 20px rgba(255, 255, 255, 0.2)',
                zIndex: 1000,
                animation: 'fadeIn 0.3s ease-out',
            }}
        >
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '12px',
            }}>
                <h3 style={{ 
                    color: colors.textPrimary, 
                    margin: 0,
                    paddingRight: '24px',
                    fontWeight: 400,
                }}>
                    {title}
                </h3>
                <button
                    onClick={onClose}
                    style={{
                        background: 'none',
                        border: 'none',
                        color: colors.textPrimary,
                        cursor: 'pointer',
                        fontSize: '24px',
                        padding: '0 8px',
                        marginTop: '-8px',
                        marginRight: '-8px',
                        flexShrink: 0,
                        fontWeight: 300,
                    }}
                >
                    ×
                </button>
            </div>
            {content && (
                <p style={{ 
                    color: colors.textSecondary,
                    margin: 0,
                    lineHeight: '1.5',
                    fontWeight: 300,
                }}>
                    {content}
                </p>
            )}
        </div>
    );
};

// StarField filters thoughts by the "Starfield" tag — those are tagged, hence
// triaged, hence NOT in the home page's drafts-only array. It must fetch the
// full corpus itself or it would render empty.
const StarField = () => {
    const { thoughts } = useAllThoughts();
    const [stars, setStars] = useState([]);
    const [selectedThought, setSelectedThought] = useState(null);
    const navigate = useNavigate();
    const containerRef = useRef(null);
    
    // Filter thoughts to only include those with the "Starfield" tag
    const starfieldThoughts = thoughts.filter(thought => 
        thought.tags && 
        Array.isArray(thought.tags) && 
        thought.tags.includes("Starfield")
    );
    
    // State for zoom and pan
    const [transform, setTransform] = useState({
        scale: 1,
        translateX: 0,
        translateY: 0
    });
    const [lastDistance, setLastDistance] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [lastPosition, setLastPosition] = useState(null);
    const [lastCenter, setLastCenter] = useState(null);
    const [visible, setVisible] = useState(false);

    const generateStars = useCallback(() => {
        const newStars = starfieldThoughts.map((thought) => ({
            x: Math.random() * 95 + '%',
            y: Math.random() * 95 + '%',
            size: Math.random() * 3 + 2,
            thought
        }));
        setStars(newStars);
    }, [starfieldThoughts]);

    useEffect(() => {
        generateStars();
    }, [generateStars]);

    useEffect(() => {
        const timer = setTimeout(() => setVisible(true), 10);
        return () => clearTimeout(timer);
    }, []);

    // Calculate center point between two touches
    const getTouchCenter = (touch1, touch2) => ({
        x: (touch1.clientX + touch2.clientX) / 2,
        y: (touch1.clientY + touch2.clientY) / 2
    });

    // Handle touch events for pinch zoom and two-finger pan
    const handleTouchStart = (e) => {
        if (e.touches.length === 2) {
            // Store initial distance for pinch-to-zoom
            const distance = Math.hypot(
                e.touches[0].clientX - e.touches[1].clientX,
                e.touches[0].clientY - e.touches[1].clientY
            );
            setLastDistance(distance);

            // Store initial center point for two-finger pan
            const center = getTouchCenter(e.touches[0], e.touches[1]);
            setLastCenter(center);
        } else if (e.touches.length === 1) {
            setIsDragging(true);
            setLastPosition({
                x: e.touches[0].clientX,
                y: e.touches[0].clientY
            });
        }
    };

    const handleTouchMove = (e) => {
        if (e.touches.length === 2) {
            e.preventDefault(); // Prevent browser zoom
            
            const currentCenter = getTouchCenter(e.touches[0], e.touches[1]);
            const distance = Math.hypot(
                e.touches[0].clientX - e.touches[1].clientX,
                e.touches[0].clientY - e.touches[1].clientY
            );
            
            // Handle both pinch-zoom and two-finger pan
            if (lastDistance && lastCenter) {
                // Calculate zoom
                const delta = distance - lastDistance;
                const newScale = Math.max(1, Math.min(4, transform.scale + delta * 0.01));
                
                // Calculate pan - adjust for current scale
                const deltaX = (currentCenter.x - lastCenter.x) / transform.scale;
                const deltaY = (currentCenter.y - lastCenter.y) / transform.scale;
                
                setTransform(prev => ({
                    scale: newScale,
                    translateX: prev.translateX + deltaX,
                    translateY: prev.translateY + deltaY
                }));
            }
            
            setLastDistance(distance);
            setLastCenter(currentCenter);
        } else if (e.touches.length === 1 && isDragging && lastPosition) {
            // Adjust single-finger pan for current scale
            const deltaX = (e.touches[0].clientX - lastPosition.x) / transform.scale;
            const deltaY = (e.touches[0].clientY - lastPosition.y) / transform.scale;
            
            setTransform(prev => ({
                ...prev,
                translateX: prev.translateX + deltaX,
                translateY: prev.translateY + deltaY
            }));
            
            setLastPosition({
                x: e.touches[0].clientX,
                y: e.touches[0].clientY
            });
        }
    };

    const handleTouchEnd = () => {
        setLastDistance(null);
        setLastCenter(null);
        setIsDragging(false);
        setLastPosition(null);
    };

    return (
        <div
            ref={containerRef}
            style={{
                position: 'fixed',
                top: 60,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: colors.background,
                overflow: 'hidden',
                padding: '20px',
                touchAction: 'none',
                animation: 'fadeIn 0.3s cubic-bezier(0.2, 0, 0, 1)',
                opacity: visible ? 1 : 0,
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onClick={(e) => {
                if (e.target === e.currentTarget) {
                    setSelectedThought(null);
                }
            }}
        >
            {/* Gradient overlay */}
            <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '40px',
                background: `linear-gradient(to bottom, 
                    ${colors.background} 0%,
                    rgba(0, 0, 0, 0) 100%
                )`,
                pointerEvents: 'none',
                zIndex: 2,
            }} />
            
            <button
                onClick={() => navigate('/')}
                style={{
                    position: 'fixed',
                    top: '20px',
                    left: '20px',
                    background: 'none',
                    border: 'none',
                    color: '#fff',
                    cursor: 'pointer',
                    zIndex: 10,
                    padding: '8px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 12H5M12 19l-7-7 7-7"/>
                </svg>
            </button>
            
            <div style={{
                transform: `scale(${transform.scale}) translate(${transform.translateX}px, ${transform.translateY}px)`,
                transformOrigin: 'center center',
                transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                width: '100%',
                height: '100%',
                position: 'relative'
            }}>
                {stars.map((star, index) => (
                    <Star
                        key={index}
                        {...star}
                        onStarClick={(thought) => setSelectedThought(thought)}
                    />
                ))}
            </div>
            
            <ThoughtPreview
                thought={selectedThought}
                onClose={() => setSelectedThought(null)}
            />
            
            <style>
                {`
                    @keyframes fadeIn {
                        from { opacity: 0; }
                        to { opacity: 1; }
                    }
                `}
            </style>
        </div>
    );
};

export default StarField; 